<script>
import { Terminal } from 'xterm'
import { FitAddon } from 'xterm-addon-fit'
import 'xterm/css/xterm.css'

export default {
   name: 'ScrapLogModal',
   props: {
      value: {
         type: Boolean,
         default: false,
      },
      nib: {
         type: [String, Number],
         default: '',
      },
   },
   data() {
      return {
         abortController: null,
         reader: null,
         terminal: null,
         fitAddon: null,
         finished: false,
         startOnShown: false,
      }
   },
   watch: {
      value(visible) {
         if (visible) {
            this.finished = false
            this.startOnShown = true
         } else {
            this.startOnShown = false
            this.closeStream()
         }
      },
      nib(newNib, oldNib) {
         if (this.value && newNib !== oldNib) {
            this.finished = false
            this.startStream()
         }
      },
   },
   beforeDestroy() {
      this.closeStream()
      if (this.terminal) {
         this.terminal.dispose()
      }
   },
   methods: {
      startStream() {
         if (!this.value || !this.$refs.terminalContainer) {
            return
         }

         this.closeStream()
         if (this.terminal) {
            this.terminal.dispose()
            window.removeEventListener('resize', this.handleResize)
         }

         this.terminal = new Terminal({
            cursorBlink: false,
            fontSize: 14,
            fontFamily: 'Consolas, "Courier New", monospace',
            theme: {
               background: '#021a23',
               foreground: '#d4d4d4',
            },
         })
         this.fitAddon = new FitAddon()
         this.terminal.loadAddon(this.fitAddon)
         this.terminal.open(this.$refs.terminalContainer)
         window.addEventListener('resize', this.handleResize)

         this.fitAddon.fit()
         this.write(`Memulai scrap NIB ${this.nib}`)

         this.openStream()
      },
      onModalShown() {
         // eslint-disable-next-line no-console
         console.log('[ScrapLogModal] onModalShown', this.startOnShown, this.nib)
         if (this.startOnShown) {
            this.startOnShown = false
            this.startStream()
         }
      },
      async openStream() {
         // eslint-disable-next-line no-console
         console.log('[ScrapLogModal] openStream start', this.nib)
         const baseURL = process.env.VUE_APP_BASE_URL || '/'
         const endpoint = `${baseURL.replace(/\/$/, '')}/oss/scrap/stream`
         const user = JSON.parse(localStorage.getItem('userData') || '{}')
         this.abortController = new AbortController()

         try {
            const response = await fetch(endpoint, {
               method: 'POST',
               credentials: 'include',
               headers: {
                  'Content-Type': 'application/json',
                  ...(user.token ? { Authorization: `Bearer ${user.token}` } : {}),
               },
               body: JSON.stringify({ nib: this.nib }),
               signal: this.abortController.signal,
            })

            if (!response.ok) {
               throw new Error(`HTTP ${response.status} (${response.statusText})`)
            }

            // eslint-disable-next-line no-console
            console.log('[ScrapLogModal] response ok', response.status)
            if (!response.body) {
               throw new Error('Response body is empty')
            }
            const reader = response.body.getReader()
            this.reader = reader
            const decoder = new TextDecoder()
            let buffer = ''

            // Read the SSE response stream until the server closes it or closeStream() replaces the reader.
            while (this.reader === reader) {
               // eslint-disable-next-line no-await-in-loop
               const { done, value } = await reader.read()
               // eslint-disable-next-line no-console
               console.log('[ScrapLogModal] chunk', done, value?.length)
               buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
               const events = buffer.split('\n\n')
               buffer = events.pop()
               events.forEach(event => this.handleSseEvent(event))
               if (done) {
                  break
               }
            }
         } catch (error) {
            if (error.name !== 'AbortError') {
               this.onError({ data: JSON.stringify({ message: error.message }) })
            }
         } finally {
            this.reader = null
            this.abortController = null
         }
      },
      handleSseEvent(rawEvent) {
         const lines = rawEvent.split(/\r?\n/)
         const eventName = lines.find(line => line.indexOf('event:') === 0)?.slice(6).trim()
         const data = lines.filter(line => line.indexOf('data:') === 0)
            .map(line => line.slice(5).trim())
            .join('\n')
         // eslint-disable-next-line no-console
         console.log('[ScrapLogModal] event', eventName, data.slice(0, 200))
         if (eventName && data) {
            this[`on${eventName.charAt(0).toUpperCase()}${eventName.slice(1)}`]({ data })
         }
      },
      onLog(event) {
         const data = this.parseEvent(event)
         this.write(data?.message ?? data)
      },
      onDone(event) {
         const data = this.parseEvent(event)
         this.write(`Selesai: ${this.stringify(data)}`)
         this.finished = true
         this.closeStream()
         this.$emit('done', data)
      },
      onError(event) {
         // Native EventSource errors can contain no data, so parsing is defensive.
         const data = this.parseEvent(event)
         this.write(`Error: ${data?.message || 'Koneksi stream terputus'}`)
         this.finished = true
         this.closeStream()
         this.$emit('error', data)
      },
      parseEvent(event) {
         try {
            return JSON.parse(event?.data)
         } catch (error) {
            return event?.data || {}
         }
      },
      stringify(data) {
         return typeof data === 'string' ? data : JSON.stringify(data)
      },
      write(message) {
         if (this.terminal) {
            this.terminal.write(`${String(message)}\n\r`)
         }
      },
      handleResize() {
         if (this.fitAddon) {
            this.fitAddon.fit()
         }
      },
      closeStream() {
         if (this.reader) {
            this.reader.cancel().catch(() => {})
            this.reader = null
         }
         if (this.abortController) {
            this.abortController.abort()
            this.abortController = null
         }
      },
      close() {
         this.closeStream()
         this.$emit('input', false)
         this.$emit('close')
      },
   },
}
</script>

<template>
   <b-modal
      :visible="value"
      centered
      size="lg"
      title="Log Scrap NIB"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      :hide-footer="false"
      @shown="onModalShown"
      @hide="close"
   >
      <div ref="terminalContainer" class="scrap-terminal" />
      <template #modal-footer>
         <b-button variant="secondary" :disabled="!finished" @click="close">
            Close
         </b-button>
      </template>
   </b-modal>
</template>

<style scoped>
.scrap-terminal {
   min-height: 360px;
   max-height: 60vh;
   overflow: hidden;
   background: #021a23;
}
</style>
